import { NEW_TABLE_RESET } from '../TableModel';
import type { TableConstructionProgress } from '../TableModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { FilingsWidgetModel, FilingsWidgetViewDetails } from './index';
import type { FilingsSettings } from './settings';
import { Duration } from '@js-joda/core';
import type { LocalDate } from '@js-joda/core';
import { batch, computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { hashKey } from '@tanstack/query-core';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import { RowIdField } from '@thinkalpha/table-client';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { t } from 'i18next';
import type { IdeaDTO } from 'src/dtos/Idea';
import type { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import type { FilingsTimeList } from 'src/features/filings-list/filingsAdapter';
import { filingsQuery } from 'src/features/filings-list/filingsAdapter';
import { filingsGridColumns } from 'src/features/filings-list/grid/filingsGridColumns';
import { createFilingsList } from 'src/features/filings-list/model';
import { inject, injectable } from 'src/features/ioc';
import type { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { ideaDTOToStage } from 'src/features/table/lib/ideaStages';
import type { TableStatus } from 'src/features/table/types';
import { TimeListColumns } from 'src/features/time-list-grid/TimeListColumns';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { hideFastMovingIdeasMenuInNewsAndEvents } from 'src/lib/featureFlags/flags/hideFastMovingIdeasMenuInNewsAndEvents';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage, UnionStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { TimeListWidgetInit } from 'src/models/TimeListWidgetState';
import { TimeListWidgetState } from 'src/models/TimeListWidgetState';
import { TimeListIdeaSelection } from 'src/models/TimeListWidgetState/ideas';
import type { SlowIdeaInput } from 'src/models/TimeListWidgetState/ideas';
import { componentFailed, timeListRuntimeReducer } from 'src/models/TimeListWidgetState/runtime';
import type { TimeListRuntimeState, TimeListRuntimeAction } from 'src/models/TimeListWidgetState/runtime';
import { filingsWidgetStateSchema } from 'src/models/TimeListWidgetState/schemas';
import type { FilingsWidgetState } from 'src/models/TimeListWidgetState/schemas';
import { filingTypesQuery, filingTagsQuery } from 'src/queries/filings';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import { filterToAllowedTypes } from 'src/routes/widgets/FilingsWidget/components/FilingTypeMenu/filingTypeAllowlist';
import { decodeSelfOwnedWidgetState } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { SlowIdeaSource } from 'src/types/slowIdeaSource';

const DEFAULT_SYMBOL_RETENTION = Duration.ofSeconds(0);

@injectable()
export class FilingsWidgetModelImpl implements FilingsWidgetModel {
    static readonly stateSchema = filingsWidgetStateSchema;
    static defaultState(): FilingsWidgetState {
        return this.stateSchema.decode({});
    }
    static fromJSON(container: ReactWidgetContainer): FilingsWidgetModelImpl {
        const persisted = decodeSelfOwnedWidgetState(this.stateSchema, container.get('GetInitialData')());
        container.bind('FilingsWidgetInit').toConstantValue({
            state: { ...persisted, fastIdeasIds: [], gotoDate: null },
            host: {
                mode: 'workspace',
                tabId: container.get('WidgetId'),
                widgetContainer: container.get('WidgetContainerController'),
                workspaceService: container.get('WorkspaceService'),
                logger: container.get('Logger'),
            },
        });
        const model = container.get('FilingsWidgetModel') as FilingsWidgetModelImpl;
        void model.init().catch((error: unknown) => {
            container.get('Logger').error('Failed to initialize filings widget', { error });
        });
        return model;
    }

    readonly #disposableStack = new DisposableStack();
    readonly #settings: TimeListWidgetState<FilingsSettings, FilingsWidgetState>;
    readonly #ideas: TimeListIdeaSelection;
    readonly #symbolFilter = computed(() => this.#settings.settings.value.symbolFilter);
    readonly #symbolRetention = computed(() => {
        const duration = this.#settings.settings.value.symbolRetention;
        return duration ? Duration.parse(duration) : DEFAULT_SYMBOL_RETENTION;
    });
    readonly #slowIdeaSourceSignal = computed(() => this.#ideas.slowSource.value);
    readonly #slowIdeaDTO = computed(() => {
        const source = this.#ideas.slowSource.value;
        return source?.type === 'idea' ? source.dto : null;
    });
    readonly #symbols = computed(() => {
        const state = this.#stateSignal.value;
        return state.type === 'ready' ? state.symbols : new Set<string>();
    });
    readonly #viewDetailsSignal = computed<FilingsWidgetViewDetails>(() => {
        const state = this.#stateSignal.value;
        if (state.type === 'error') return state;
        if (state.type === 'initializing') return { type: 'loading' };
        if (state.type === 'waiting-for-symbols') return { type: 'waiting-for-symbols' };
        return {
            type: 'ready',
            instanceId: state.instanceId,
            symbols: this.#symbols,
            symbolFilter: this.#symbolFilter,
            fastIdeas: this.#ideas.fastIdeas,
        };
    });
    get settings(): ReadonlySignal<FilingsSettings> {
        return this.#settings.settings;
    }
    get dirty(): ReadonlySignal<boolean> {
        return this.#settings.dirty;
    }
    toJSON(): FilingsWidgetState {
        return this.#settings.toJSON();
    }
    markClean(): void {
        this.#settings.markClean();
    }
    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    readonly gridColumns: TimeListColumns;
    readonly filingsList: FilingsTimeList;
    #logger: Logger;
    #featureFlagService: ReactBindings['FeatureFlagService'];
    #queryClient: ReactBindings['QueryClient'];
    #table: ReactBindings['TableModel'];
    #slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);
    #rowIdToSymbolMap = new Map<number, string>();
    #unresolvedSymbolRowIds = new Set<number>();
    #symbolChangeDirectionsMap = signal<Map<string, number>>(new Map());
    #stateSignal = signal<TimeListRuntimeState>({ type: 'initializing' });
    #devtools: ReducerDevtoolsConnection<TimeListRuntimeState, TimeListRuntimeAction> | undefined;
    readonly #gotoDateSignal = computed(() => this.#settings.settings.value.gotoDate);
    readonly #fastIdeas = computed(() => this.#ideas.fastIdeas.value);
    readonly #filingTypes = signal<string[]>([]);
    readonly #filingTags = signal<string[]>([]);
    readonly #selectedFilingTypes = computed(() => filterToAllowedTypes(this.#settings.settings.value.filingTypes));
    readonly #selectedFilingTags = signal<string[]>([]);
    readonly #summary = signal<{
        id: string | null;
        context: NonNullable<Parameters<FilingsWidgetModel['onRowClick']>[1]> | null;
    }>({ id: null, context: null });
    readonly #showFilingSummary = computed(() => this.#summary.value.id);
    readonly #filingSummaryContext = computed(() => this.#summary.value.context);

    readonly #connectionStatus = computed(() => ({
        status: this.#table.connectionStatus.value,
        progress: this.#table.progress.value,
        failureCount: this.#table.failureCount.value,
    }));

    filingsControlsHeaderElements: WidgetHeaderElement[] = [
        { type: 'overflowDropdown', position: 0, alwaysVisible: true },
        { type: 'spacer', position: 5, alwaysVisible: true },
        { type: 'filingsGoToDate', position: 10, priority: 1 },
        { type: 'filingsFilingTypeFilter', position: 60, priority: 2 },
        { type: 'filingsFilingTagsFilter', position: 70, priority: 3 },
    ];

    headerElements!: WidgetHeaderElement[];

    eventBus!: FilingsWidgetModel['eventBus'];

    constructor(
        @inject('FilingsWidgetInit') init: TimeListWidgetInit<FilingsSettings>,
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('Logger') logger: Logger,
        @inject('DefaultUniverseServiceProvider')
        defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('TableModel') table: ReactBindings['TableModel'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') formulaService: ReactBindings['FormulaService'],
    ) {
        this.#disposableStack.use(table);
        this.#settings = this.#disposableStack.use(
            new TimeListWidgetState(init.host, init.state, (state) => filingsWidgetStateSchema.decode(state)),
        );
        this.filingsList = this.#disposableStack.use(createFilingsList(queryClient));
        this.#ideas = this.#disposableStack.use(
            new TimeListIdeaSelection(
                this.#settings.settings,
                (selection) => this.#settings.update(selection),
                (selection) => this.#settings.initialize(selection),
                (id) => queryClient.fetchQuery(getIdeaDTOByIdQuery(queryClient, formulaService, slugMapService, id)),
                async () => (await defaultUniverseServiceProvider()).universe.value.id,
            ),
        );
        this.gridColumns = this.#disposableStack.use(
            new TimeListColumns(filingsGridColumns, {
                preferences: computed(() => this.#settings.settings.value.columns ?? []),
                update: (columns) => this.#settings.update({ columns }),
            }),
        );
        this.#featureFlagService = featureFlagService;
        this.#queryClient = queryClient;
        this.#table = table;
        this.#slugMapServiceProvider = slugMapServiceProvider;
        this.#logger = logger.getSubLogger({ name: 'FilingsWidgetModel' });

        // Build the EventBus for the new header system
        this.eventBus = this.#buildEventBus();
    }

    async init() {
        const tabId = this.#settings.host.mode === 'workspace' ? this.#settings.host.tabId : undefined;
        const baseElements = tabId
            ? COMMON_HEADER_ELEMENTS
            : STANDALONE_COMMON_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = [
            ...baseElements,
            { type: 'symbolPicker', position: 25, priority: 80, placeholder: 'symbol' },
            { type: 'symbolRetention', position: 28, priority: 50 },
            {
                type: 'ideasMenu',
                key: 'slow',
                position: 54,
                priority: 60,

                selectType: 'single',
                ideaType: 'slow',
                ideaPlanTypes: ['universe', 'screener', 'watchlist'],
                showSelected: false,
                allowDelete: false,
            },
            {
                type: 'ideasMenu',
                key: 'fast',
                position: 56,
                priority: 60,

                selectType: 'multi',
                ideaType: 'fast',
                ideaPlanTypes: ['scanner', 'if-then'],
                showSelected: true,
                allowDelete: false,
            },
            { type: 'connectionStatus', position: 995, priority: 995 },
        ];

        this.#devtools = connectReducerDevtools({ name: `Filings [${tabId ?? 'standalone'}]` });
        if (this.#devtools) {
            this.#disposableStack.use(this.#devtools);
            this.#devtools.init(this.#stateSignal.value);
        }

        // Check feature flags and update header elements
        const hideFastMovingIdeasMenu = this.#featureFlagService.getValue(hideFastMovingIdeasMenuInNewsAndEvents);

        // Filter out fast ideas menu if feature flag is enabled
        this.headerElements = this.headerElements.filter((element) => {
            // Remove fast ideas menu if hideFastMovingIdeasMenu is true
            if (element.type === 'ideasMenu' && element.ideaType === 'fast') {
                return !hideFastMovingIdeasMenu;
            }
            return true;
        });

        await this.#ideas.loadInitial();
        if (this.#disposableStack.disposed) return;
        const slugMapService = await this.#slugMapServiceProvider();
        if (this.#disposableStack.disposed) return;
        const symbolSlug = computed(() => slugMapService.slugMap.value[Slugs.symbol]);
        const changeCloseSlug = computed(() => slugMapService.slugMap.value[Slugs.symbolChangeClose]);
        const [filingTypes, filingTags] = await Promise.all([
            this.#queryClient.fetchQuery(filingTypesQuery()),
            this.#queryClient.fetchQuery(filingTagsQuery()),
        ]);
        if (this.#disposableStack.disposed) return;
        this.#filingTypes.value = filterToAllowedTypes(filingTypes);
        this.#filingTags.value = filingTags;
        this.#updateState({ type: 'INITIALIZE_SUCCESS' });

        const queryOptions: ReadonlySignal<QueryObserverOptions<FetchableTableCreationResultWithColumns>> = computed(
            () => {
                const symbolFilterValue = this.symbolFilter?.value ?? null;
                const source = this.#slowIdeaSourceSignal.value;
                const fastIdeasValue = this.#fastIdeas.value ?? [];
                return {
                    queryKey: [
                        'filings-widget',
                        'table',
                        symbolFilterValue,
                        source?.type ?? null,
                        source?.id ?? null,
                        fastIdeasValue.map((idea) => idea.idea.id),
                    ],
                    queryFn: async (_context, debugIdentifier?: string) => {
                        let stage: Stage;
                        if (symbolFilterValue) {
                            stage = SourceStage.allForKey('ticker');
                        } else if (source?.type === 'idea') {
                            const allStage = SourceStage.allForKey('ticker');
                            stage = ideaDTOToStage(allStage, source.dto);
                        } else if (!source) {
                            stage = SourceStage.allForKey('ticker');
                        } else {
                            stage = SourceStage.fromTickerUniverseId(source.id);
                        }

                        if (symbolFilterValue) {
                            stage = FilterStage.fromInputStage(stage, {
                                type: 'filter',
                                formula: `symbol == "${symbolFilterValue}"`,
                                imports: [{ ...symbolSlug.value, alias: 'symbol' }],
                            });
                        } else {
                            if (fastIdeasValue.length > 1) {
                                const stages = fastIdeasValue.map((idea) => ideaDTOToStage(stage, idea));

                                if (stages.length > 0) {
                                    stage = UnionStage.from(stages);
                                }
                            } else if (fastIdeasValue.length === 1) {
                                const idea = fastIdeasValue[0];
                                stage = ideaDTOToStage(stage, idea);
                            }
                        }

                        const formula = 'changeClose > 0 ? 1 : changeClose < 0 ? -1 : 0';
                        const imports = [{ ...changeCloseSlug.value, alias: 'changeClose' }];

                        stage = OutputStage.fromInputStage(stage, {}, [
                            { id: 'symbol', type: 'indicator', indicator: symbolSlug.value },
                            { id: 'changeDirection', type: 'formula', formula, imports },
                        ]);

                        return {
                            ...(await createTable({ leafStage: stage.build() }, undefined, debugIdentifier)),
                            fetchMode: undefined,
                        };
                    },
                    enabled: true,
                    staleTime: 0,
                    gcTime: 0,
                    refetchOnWindowFocus: false,
                };
            },
        );

        // Table replacement waits for a fresh membership image; date/type changes reuse known membership.
        const tableOptions = signal(queryOptions.peek());
        let tableKey: string | undefined;
        let sourceKey: string | undefined;
        this.#disposableStack.defer(
            effect(() => {
                const options = queryOptions.value;
                const nextTableKey = hashKey(options.queryKey!);
                const query = filingsQuery(
                    this.gotoDateSignal.value,
                    this.selectedFilingTypes.value,
                    this.selectedFilingTags.value,
                );
                const nextKey = hashKey([nextTableKey, query]);
                if (nextKey !== sourceKey) {
                    const source = {
                        sourceId: nextKey,
                        selectionKind: this.symbolFilter?.peek()
                            ? ('single-symbol' as const)
                            : ('multi-symbol' as const),
                        mode: query.anchor ? ('historical' as const) : ('live' as const),
                        query,
                        retention: this.symbolRetention?.peek().toMillis() ?? 0,
                    };
                    const status = this.filingsList.state.peek().source.status;
                    if (nextTableKey !== tableKey || status === 'awaiting-table-reset') {
                        untracked(() => this.#updateState({ type: 'SOURCE_CHANGED' }));
                        this.filingsList.beginSource(source);
                    } else if (status === 'awaiting-symbols') {
                        this.filingsList.beginSource(source);
                        this.filingsList.membershipReset();
                    } else {
                        this.filingsList.activateSource(source, this.filingsList.state.peek().activeSymbols);
                    }
                    tableKey = nextTableKey;
                    sourceKey = nextKey;
                }
                tableOptions.value = options;
            }),
        );
        this.#disposableStack.defer(
            effect(() => {
                this.filingsList.setRetention(this.symbolRetention?.value.toMillis() ?? 0);
            }),
        );
        this.#table.init({
            initBounds: { firstRow: 0, windowSize: 100000 },
            initThrottle: 1000,
            queryOptions: tableOptions,
            devtoolsName: tabId ? 'Filings/table' : 'Filings/standalone/table',
        });

        this.#disposableStack.use(
            this.#table.updatesWithReset$.subscribe((updates) => {
                if (!updates) return;
                if (TRACE) {
                    this.#logger.trace('Filings membership batch received', {
                        kind: updates === NEW_TABLE_RESET ? 'reset' : updates.type,
                        sourceStatus: this.filingsList.state.peek().source.status,
                        tableKey: this.#table.key,
                    });
                }

                if (updates === NEW_TABLE_RESET) {
                    this.filingsList.membershipReset();
                    this.#rowIdToSymbolMap.clear();
                    this.#unresolvedSymbolRowIds.clear();
                    this.#symbolChangeDirectionsMap.value = new Map();
                    return;
                }

                if (this.filingsList.state.peek().source.status === 'awaiting-table-reset') return;
                if (updates.type === 'quote' && this.filingsList.state.peek().source.status === 'awaiting-symbols') {
                    return;
                }

                if (updates.type === 'snapshot') {
                    this.#rowIdToSymbolMap.clear();
                    this.#unresolvedSymbolRowIds.clear();
                    const newSymbols = new Set<string>();
                    // Create a new Map to avoid modifying the one in the signal
                    const changeDirections = new Map<string, number>();

                    for (const update of updates) {
                        this.#rowIdToSymbolMap.set(update[RowIdField]!, update.symbol);
                        newSymbols.add(update.symbol);

                        // Get change direction directly from the changeDirection field
                        const changeDirection = Number(update['changeDirection']);
                        if (!isNaN(changeDirection)) {
                            changeDirections.set(update.symbol, changeDirection);
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                    if (this.filingsList.state.peek().source.status === 'awaiting-symbols') {
                        this.filingsList.activateSymbols([...newSymbols]);
                    } else this.filingsList.publishSymbols([...newSymbols]);

                    this.#updateState({
                        type: 'SYMBOLS_SNAPSHOT_RECEIVED',
                        symbols: newSymbols,
                    });
                    return;
                } else if (updates.type === 'diff') {
                    const previousSymbols = new Set(this.filingsList.state.peek().activeSymbols);
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.peek());
                    for (const rowId of updates.deletedRowIds) {
                        this.#rowIdToSymbolMap.delete(rowId);
                        this.#unresolvedSymbolRowIds.delete(rowId);
                    }
                    for (const rowId of updates.orderedRowIds) {
                        if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                    }
                    for (const rowId of updates.rowIdToRowIndex.keys()) {
                        if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                    }
                    for (const update of updates) {
                        const rowId = update[RowIdField]!;
                        const symbol = update.symbol;
                        if (!symbol) {
                            if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                            continue;
                        }
                        this.#rowIdToSymbolMap.set(rowId, symbol);
                        this.#unresolvedSymbolRowIds.delete(rowId);
                        const direction = Number(update.changeDirection);
                        if (!isNaN(direction)) changeDirections.set(symbol, direction);
                    }
                    // Only explicit deletions remove membership. Missing ordering entries are not removals.
                    // Keep unresolved IDs across batches until their values arrive or they are deleted.
                    const symbols = new Set(this.#rowIdToSymbolMap.values());
                    for (const symbol of changeDirections.keys()) {
                        if (!symbols.has(symbol)) changeDirections.delete(symbol);
                    }
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                    if (this.#unresolvedSymbolRowIds.size > 0) {
                        if (TRACE) {
                            this.#logger.trace('Filings membership awaiting complete symbol values', {
                                unresolvedRows: this.#unresolvedSymbolRowIds.size,
                                knownRows: this.#rowIdToSymbolMap.size,
                            });
                        }
                        return;
                    }
                    if (this.filingsList.state.peek().source.status === 'awaiting-symbols') {
                        this.filingsList.activateSymbols([...symbols]);
                        this.#updateState({ type: 'SYMBOLS_SNAPSHOT_RECEIVED', symbols });
                        return;
                    }
                    // Compare complete memberships, preserving delete/re-add and duplicate symbol rows.
                    const added = symbols.difference(previousSymbols);
                    const removed = previousSymbols.difference(symbols);
                    this.filingsList.updateSymbols([...added], [...removed]);
                    this.#updateState({ type: 'SYMBOLS_UPDATE_RECEIVED', added, removed });
                } else if (updates.type === 'quote') {
                    // Create a new Map from the current change directions
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.value);

                    // Handle quote updates
                    for (const update of updates) {
                        const symbol = update.symbol;
                        if (symbol) {
                            // Get change direction directly from the changeDirection field
                            const changeDirection = Number(update['changeDirection']);
                            if (!isNaN(changeDirection)) {
                                changeDirections.set(symbol, changeDirection);
                            }
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                }
            }),
        );
    }

    readonly runtimeError = computed(() => {
        const state = this.#stateSignal.value;
        return state.type === 'error' ? state.error : undefined;
    });

    reportComponentError(error: Error): void {
        this.#updateState(componentFailed(error));
    }

    get viewDetails(): ReadonlySignal<FilingsWidgetViewDetails> {
        return this.#viewDetailsSignal;
    }

    get symbols(): ReadonlySignal<ReadonlySet<string> | null> {
        return this.#symbols;
    }

    get symbolFilter(): ReadonlySignal<string | null> {
        return this.#symbolFilter;
    }

    get symbolRetention(): ReadonlySignal<Duration> {
        return this.#symbolRetention;
    }

    get symbolChangeDirections(): ReadonlySignal<ReadonlyMap<string, number>> {
        return this.#symbolChangeDirectionsMap;
    }

    onChangeSymbolFilter(value: string) {
        // Ignore unchanged user selections, including equivalent empty values.
        const currentValue = this.#settings.peek().symbolFilter;
        if ((value === '' && currentValue === null) || value === currentValue) {
            return;
        }
        this.#settings.update({ symbolFilter: value === '' ? null : value });
    }

    onChangeSymbolRetention(duration: Duration) {
        this.#settings.update({ symbolRetention: duration.toString() });
    }

    get slowIdeaSource(): ReadonlySignal<SlowIdeaSource | null> {
        return this.#slowIdeaSourceSignal;
    }

    get fastIdeas(): ReadonlySignal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]> {
        return this.#ideas.fastIdeas;
    }

    onSelectSlowIdeas(ideas: SlowIdeaInput[]): void {
        this.#ideas.selectSlow(ideas);
    }

    onSelectFastIdeas(ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]): void {
        this.#ideas.selectFast(ideas);
    }

    onSelectSymbol(symbol: string) {
        if (this.#settings.host.mode === 'standalone') this.#settings.update({ symbolFilter: symbol });
        else this.#settings.onSelectSymbol(symbol);
    }

    openChartForSymbol(symbol: string) {
        this.#settings.openChartForSymbol(symbol);
    }

    openLensForSymbol(symbol: string) {
        this.#settings.openLensForSymbol(symbol);
    }

    get showFilingSummary(): ReadonlySignal<string | null> {
        return this.#showFilingSummary;
    }

    get filingSummaryContext(): ReadonlySignal<{
        date: string;
        submittedTime: string;
        uploadedTime: string;
        symbol: string;
        type: string;
        filingName: string;
        eventDetails: string | null;
        link: string;
        changeDirection: 'up' | 'down' | undefined;
    } | null> {
        return this.#filingSummaryContext;
    }

    onRowClick(
        filingId: string | null,
        context?: {
            date: string;
            submittedTime: string;
            uploadedTime: string;
            symbol: string;
            type: string;
            filingName: string;
            eventDetails: string | null;
            link: string;
            changeDirection: 'up' | 'down' | undefined;
        },
    ) {
        // Toggle behavior: if clicking the same filing, close it
        const currentFilingId = this.#showFilingSummary.value;
        const newFilingId = currentFilingId === filingId ? null : filingId;
        this.#summary.value = { id: newFilingId, context: newFilingId ? (context ?? null) : null };
    }

    get filingTypes(): ReadonlySignal<string[]> {
        return this.#filingTypes;
    }

    get filingTags(): ReadonlySignal<string[]> {
        return this.#filingTags;
    }

    get selectedFilingTypes(): ReadonlySignal<string[]> {
        return this.#selectedFilingTypes;
    }

    get selectedFilingTags(): ReadonlySignal<string[]> {
        return this.#selectedFilingTags;
    }

    get gotoDateSignal(): ReadonlySignal<LocalDate | null> {
        return this.#gotoDateSignal;
    }

    onSelectFilingTypes(types: string[]) {
        batch(() => {
            this.#settings.update({ filingTypes: types });
            if (!types.includes('8-K')) this.#selectedFilingTags.value = [];
        });
    }

    onSelectFilingTags(tags: string[]) {
        this.#selectedFilingTags.value = tags;
    }

    onGoToDate(date: LocalDate | null) {
        this.#settings.update({ gotoDate: date });
    }

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#table.connectionStatus;
    }

    get progress(): ReadonlySignal<TableConstructionProgress> {
        return this.#table.progress;
    }

    refetch(): void {
        this.#table.refetch();
    }

    #updateState(action: TimeListRuntimeAction) {
        this.#stateSignal.value = timeListRuntimeReducer(this.#stateSignal.peek(), action);
        this.#devtools?.send(action, this.#stateSignal.peek());
    }

    #buildEventBus(): FilingsWidgetModel['eventBus'] {
        const ownEventBus = MergedEventBus.from(
            EventBusBuilder.create()
                // Filings-specific signals
                .addSignal('widget::instrument', () =>
                    computed(() => {
                        return this.#symbolFilter.value ?? '';
                    }),
                )
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::symbolRetention', () =>
                    computed(() => {
                        return this.#symbolRetention.value;
                    }),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addSignal('widget::slowIdea', () => this.#slowIdeaDTO)
                .addSignal('widget::selectedFastIdeas', () =>
                    computed(() => {
                        return this.#ideas.fastIdeas.value.map((idea) => ({
                            type: 'idea' as const,
                            id: idea.idea.id!,
                        }));
                    }),
                )
                .addSignal('widget::selectedSlowIdeas', () =>
                    computed(() => {
                        const source = this.#slowIdeaSourceSignal.value;
                        if (!source) return [];
                        return [{ type: source.type, id: source.id }];
                    }),
                )
                // Filings-specific events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.onChangeSymbolFilter(payload.instrument);
                })
                .addEvent('widget::updateSymbolRetention', (payload) => {
                    this.onChangeSymbolRetention(Duration.parse(payload.duration));
                })
                .addEvent('widget::onIdeasSelect', (payload) => {
                    // This event is only emitted for fast ideas by the IdeasMenu component
                    // (slow ideas use widget::selectSlowIdea instead)
                    const fastIdeas = payload.ideas.filter((idea) => !('universeId' in idea)) as (
                        | IdeaDTO<ScannerPlanDTO>
                        | IdeaDTO<SearchPlanDTO>
                    )[];
                    // Always forward to handler, even empty array (to clear selection)
                    this.onSelectFastIdeas(fastIdeas);
                })
                .addEvent('widget::onIdeaDelete', () => {
                    // Filings widget doesn't support idea deletion
                })
                .addEvent('widget::selectSlowIdea', (payload) => {
                    // Type assertion needed because event uses generic IdeaDTO while method expects specific plan types
                    this.onSelectSlowIdeas(payload.ideas as Parameters<typeof this.onSelectSlowIdeas>[0]);
                })
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.refetch();
                })
                // Filings second-row control signals
                .addSignal('widget::filingsGoToDate', () => this.#gotoDateSignal)
                .addSignal('widget::filingsSelectedFilingTypes', () =>
                    computed(() => {
                        return this.#selectedFilingTypes.value;
                    }),
                )
                .addSignal('widget::filingsAvailableFilingTypes', () =>
                    computed(() => {
                        return this.#filingTypes.value;
                    }),
                )
                .addSignal('widget::filingsSelectedFilingTags', () =>
                    computed(() => {
                        return this.#selectedFilingTags.value;
                    }),
                )
                .addSignal('widget::filingsAvailableFilingTags', () =>
                    computed(() => {
                        return this.#filingTags.value;
                    }),
                )
                // Filings second-row control events
                .addEvent('widget::filingsGoToDateChange', (payload) => {
                    this.onGoToDate(payload.date);
                })
                .addEvent('widget::filingsFilingTypeFilterChange', (payload) => {
                    this.onSelectFilingTypes(payload.types);
                })
                .addEvent('widget::filingsFilingTagsFilterChange', (payload) => {
                    this.onSelectFilingTags(payload.tags);
                })
                .build(),
        );
        const { channelSlice, widgetTabSlice } = this.#settings;
        return (
            channelSlice && widgetTabSlice
                ? ownEventBus.merge(channelSlice.eventBus).merge(widgetTabSlice.eventBus)
                : ownEventBus.merge(
                      EventBusBuilder.create()
                          .addSignal('widget::name', () => signal(t('widgets::filings::name')))
                          .build(),
                  )
        ) as FilingsWidgetModel['eventBus'];
    }
}
